import { type QueryClient, queryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { generateKeyBetween } from "fractional-indexing";
import { useGraphQLClient, useSubscribe } from "@package/graphql/react";
import { useParams, useNavigate } from "@tanstack/react-router";
import { usePaneActivePageId, useSoleGridPageId } from "../../webapp/pane/context";
import { usePraMockPage } from "../../webapp/market-pages/pra/praMockPage";
import type { GraphQLClient } from "@package/graphql";
import { relativeRowToRowId } from "@package/grid-utils";
import { isDeepEqual, uniqueBy } from "remeda";
import { useAuth0 } from "@auth0/auth0-react";
import { useCallback, useRef } from "react";
import type { Writable } from "type-fest";
import toast from "react-hot-toast";
import { nanoid } from "nanoid";

import { captureEvent } from "../../utils/o11y";

import type { PageFragmentFragment } from "../../__generated__/gql/graphql";
import { getSyncBrotli } from "../../utils/encoding";
import { useSuspenseFn } from "../../utils/useSuspenseFn";
import { readFragment } from "../../graphql";
import {
  PageUpdateMutation,
  PageMemberFragment,
  PageMemberUpdateMutation,
  PageMembersByPageIdsQuery,
  PageMemberInsertMany,
  PageMemberQuery,
  PageMemberSubscription,
} from "./queries";
import { getTabSessionKey } from "../cached";
import {
  computePageInsertIdx,
  DEFAULT_INSERT_PAGE_POSITION,
  useInsertPagePosition,
} from "../grid-settings/preferences";
import { useUserId } from "../../context/auth/hooks";
import { sort } from "./sort";

const getHasuraToken = (getAccessTokenSilently: ReturnType<typeof useAuth0>["getAccessTokenSilently"]) =>
  getAccessTokenSilently({ authorizationParams: { audience: "hasura" } });

// Avoid updating the current pages data from the server if there are uncommited optimistic updates.
// Since the subscription exists separately from the update hook, we need to track this at a higher scope.
const PENDING_MUTATIONS: Set<string> = new Set();

export type TPage = PageFragmentFragment & { shared?: boolean };
export type TPageMember = { page_id: string; user_id: string; page_order: string };

/** In-flight create/duplicate idxs — used so rapid inserts don't collide, without flashing tabs in the UI. */
const PENDING_PAGE_INSERTS = new Map<string, { id: string; idx: string; name: string }>();

const buildQueryKey = (userId: string) => {
  const id = getTabSessionKey();
  return ["pages", userId, id];
};

const getPagesForInsert = (qry: QueryClient, userId: string): TPage[] => {
  const cached = qry.getQueryData<TPage[]>(buildQueryKey(userId)) ?? [];
  const cachedIds = new Set(cached.map((p) => p.id));
  const pending = [...PENDING_PAGE_INSERTS.values()]
    .filter((p) => !cachedIds.has(p.id))
    .map(
      (p) =>
        ({
          id: p.id,
          idx: p.idx,
          name: p.name,
        }) as TPage,
    );
  return [...cached, ...pending].sort((a, b) => (a.idx < b.idx ? -1 : 1));
};

const reservePageInsert = (id: string, idx: string, name: string) => {
  PENDING_PAGE_INSERTS.set(id, { id, idx, name });
};

const releasePageInsert = (id: string) => {
  PENDING_PAGE_INSERTS.delete(id);
};

export const pagesQueryOptions = (hasura: GraphQLClient, qry: QueryClient, userId: string) =>
  queryOptions({
    queryKey: buildQueryKey(userId),
    queryFn: async () => {
      const pageIds = qry.getQueryData<string[]>(["page_ids", userId]) ?? [];
      const res = await hasura.execute(PageMemberQuery, { pageIds, userId });
      const data = readFragment(PageMemberFragment, res?.data?.page_member ?? []);
      const pages = uniqueBy(data.map((pm) => pm.Page) as PageFragmentFragment[], (p) => p.id);
      const pageMembers = qry.getQueryData<TPageMember[]>(["page_members", userId]) ?? data;
      return sort(userId, pages, pageMembers, hasura);
    },
  });

export const useSyncPages = () => {
  const qry = useQueryClient();
  const userId = useUserId();
  const hasura = useGraphQLClient();
  const pageIds = usePageIds(userId).data ?? [];

  useSubscribe({
    document: PageMemberSubscription,
    variables: { pageIds, userId },
    next: (res) => {
      if (PENDING_MUTATIONS.size > 0) return;
      const data = readFragment(PageMemberFragment, res?.data?.page_member ?? []);
      const pages = uniqueBy(data.map((pm) => pm.Page) as PageFragmentFragment[], (p) => p.id);
      const pageMembers = data.map((pm) => ({
        page_id: pm.page_id,
        user_id: pm.user_id,
        page_order: pm.page_order,
      }));

      // Invalidate page_products if page members have changed. This is because if user A shares a page with user B, but user B doesn't have the same products as user A, the numbers won't be calculated for them until we refetch. Page products are not keyed by pageId.
      const current = qry.getQueryData<TPageMember[]>(["page_members", userId]) ?? [];
      if (!isDeepEqual(current, pageMembers)) {
        qry.invalidateQueries({ queryKey: ["page_products"] });
        qry.invalidateQueries({ queryKey: ["page_members", userId] });
        qry.setQueryData(["page_members", userId], data);
      }

      qry.setQueryData(buildQueryKey(userId), sort(userId, pages, pageMembers, hasura));
      qry.setQueryData<string[]>(
        ["page_ids", userId],
        pages.map((p) => p.id),
      );
    },
  });
};

export const usePages = (userId: string) => useQuery(pagesQueryOptions(useGraphQLClient(), useQueryClient(), userId));

export const usePageIds = (userId: string) =>
  useQuery({
    ...pagesQueryOptions(useGraphQLClient(), useQueryClient(), userId),
    select: (data) => data.map((p) => p.id),
  });

export const usePage = (pageId: string) =>
  useQuery({
    ...pagesQueryOptions(useGraphQLClient(), useQueryClient(), useUserId()),
    select: (data) => data.find((p) => p.id === pageId),
  });

export const useUpdatePage = () => {
  const userId = useUserId();
  const qry = useQueryClient();
  const hasura = useGraphQLClient();

  const timeout = useRef<NodeJS.Timeout | null>(null);
  const updatesRef = useRef<Record<string, Partial<TPage>>>({});

  const mutation = useMutation({
    networkMode: "always",
    mutationFn: async (id: string) => {
      PENDING_MUTATIONS.delete(id);
      const { id: _id, shared: _shared, ...values } = updatesRef.current[id] ?? {};
      delete updatesRef.current[id];

      if (values.idx !== undefined) {
        // When updating the idx, we also need to update the page_member table to keep things in sync.
        await hasura.execute(PageMemberUpdateMutation, {
          userId,
          pageId: id,
          pageOrder: values.idx,
        });
      }

      return hasura.execute(PageUpdateMutation, { pageId: id, values });
    },
  });

  const { mutate } = mutation;

  const optimistic = useCallback(
    async (values: Partial<TPage> & { id: string }) => {
      PENDING_MUTATIONS.add(values.id);

      updatesRef.current[values.id] = {
        ...updatesRef.current[values.id],
        ...values,
      };

      qry.setQueryData<TPage[]>(buildQueryKey(userId), (old) =>
        old?.map((x) => (x.id === values.id ? { ...x, ...values } : x)),
      );

      if (timeout.current) clearTimeout(timeout.current);

      await qry.cancelQueries({ queryKey: buildQueryKey(userId) });

      timeout.current = setTimeout(() => mutate(values.id), 300);
    },
    [qry, userId, mutate],
  );

  return { ...mutation, optimistic };
};

/**
 * Open a page. The multi-pane shell keeps the open page per pane and leaves the
 * route at /app/market, so navigating to /app/market/$id there is bounced
 * straight back by MarketPageGate and opens nothing.
 */
export const useSwitchToPage = () => {
  const pane = usePaneActivePageId();
  const navigate = useNavigate();
  const { deactivate: leavePraMockPage } = usePraMockPage();
  return useCallback(
    (id: string) => {
      leavePraMockPage();
      if (pane) {
        pane[1](id);
        return;
      }
      navigate({ to: "/app/market/$id", params: { id } });
    },
    [pane, navigate, leavePraMockPage],
  );
};

export const useAppendNewPage = () => {
  const qry = useQueryClient();
  const userId = useUserId();
  const activePageId = useOptionalActivePageId();
  const insertPagePosition = useInsertPagePosition().data ?? DEFAULT_INSERT_PAGE_POSITION;
  const { getAccessTokenSilently } = useAuth0();
  const switchToNewPage = useSwitchToPage();
  const mutationContextRef = useRef<{ pageId?: string; switchToPage?: boolean }>({});

  return useMutation({
    networkMode: "always",
    mutationFn: async ({ name, pageId, switchToPage }: { name: string; pageId?: string; switchToPage?: boolean }) => {
      // Include in-flight inserts so rapid creates don't reuse the same idx.
      const sortedPages = getPagesForInsert(qry, userId);
      let idx = computePageInsertIdx(sortedPages, insertPagePosition, activePageId);

      // Avoid idx collisions when fractional keys repeat across pages.
      if (sortedPages.some((page) => page.idx === idx)) {
        const lastIdx = sortedPages[sortedPages.length - 1]?.idx ?? null;
        idx = generateKeyBetween(lastIdx, null);
      }

      const id = pageId || nanoid();
      reservePageInsert(id, idx, name);
      qry.setQueryData<string[]>(["page_ids", userId], (old) => [...(old ?? []), id]);
      mutationContextRef.current = { pageId: id, switchToPage };
      const token = await getHasuraToken(getAccessTokenSilently);
      const response = await toast.promise(
        fetch("/api/create-page", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            id,
            name,
            idx,
            user_id: userId,
            extra_header_height: true,
          }),
        }).then((res) => {
          if (!res.ok) throw new Error("Error creating page");
          return res;
        }),
        {
          loading: "Creating page...",
          success: "Page created",
          error: "Error creating page",
        },
      );
      return { id, response };
    },
    onError: () => {
      const { pageId } = mutationContextRef.current;
      if (!pageId) return;
      releasePageInsert(pageId);
      qry.setQueryData<string[]>(["page_ids", userId], (old) => (old ?? []).filter((pid) => pid !== pageId));
    },
    onSettled: (_data, _error, _vars) => {
      const { pageId } = mutationContextRef.current;
      if (pageId) releasePageInsert(pageId);
      qry.invalidateQueries({ queryKey: buildQueryKey(userId) });
    },
    onSuccess: async ({ id: pageId, response }) => {
      const res = (await response.json()) as { id?: string; page_name?: string; idx?: string }[];
      const pageData = Array.isArray(res) ? res[0] : res;
      const { switchToPage } = mutationContextRef.current;
      captureEvent("page_created", {
        initial_page: false,
        page_name: pageData?.page_name,
        idx: pageData?.idx,
      });
      await qry.refetchQueries({ queryKey: buildQueryKey(userId) });
      if (switchToPage && pageId) switchToNewPage(pageId);
    },
  });
};

export const useDuplicatePage = () => {
  const qry = useQueryClient();
  const userId = useUserId();
  const { getAccessTokenSilently } = useAuth0();

  return useMutation({
    networkMode: "always",
    mutationFn: async ({
      pageId,
      newPageId,
      withFormatting = true,
    }: {
      pageId: string;
      newPageId: string;
      withFormatting?: boolean;
    }) => {
      const pages = getPagesForInsert(qry, userId);
      // Source page must come from the real cache (pending stubs are idx-only).
      const page = (qry.getQueryData<TPage[]>(buildQueryKey(userId)) ?? []).find((p) => p.id === pageId);
      if (!page) throw new Error("Page not found");
      const lastPage = pages[pages.length - 1];

      // Insert to the right of the source page. computePageInsertIdx guards generateKeyBetween
      // failures when adjacent pages share the same idx (e.g. "a1 >= a1").
      let idx = computePageInsertIdx(pages, "right_of_current", pageId);
      // Avoid idx collisions when fractional keys repeat across pages.
      if (pages.some((p) => p.idx === idx)) {
        idx = generateKeyBetween(lastPage.idx, null);
      }

      const existingNames = new Set(pages.map((x) => (x.name ?? "").trim()).filter((x): x is string => x.length > 0));
      const originalName = (page.name ?? "").trim() || "Untitled";
      const base = (() => {
        const match = originalName.match(/^(.*?)(?: \((\d+)\))?$/);
        if (!match) return originalName;
        const [, b] = match;
        return (b ?? originalName).trim();
      })();
      let i = 1;
      let candidate = `${base} (${i})`;
      while (existingNames.has(candidate)) {
        i += 1;
        candidate = `${base} (${i})`;
      }

      // Omit client-only `shared` without mutating the cached page object
      const { shared: _shared, ...pageWithoutShared } = page;
      const now = new Date().toISOString();

      // Reserve idx without adding a tab to the UI cache (avoids appear/disappear flash).
      reservePageInsert(newPageId, idx, candidate);
      qry.setQueryData<string[]>(["page_ids", userId], (old) => [...(old ?? []), newPageId]);

      const token = await getHasuraToken(getAccessTokenSilently);
      return toast.promise(
        fetch("/api/create-page", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            ...pageWithoutShared,
            user_id: userId,
            id: newPageId,
            idx,
            name: candidate,
            adhoc_spreads: undefined,
            cell_highlights: withFormatting ? page.cell_highlights : undefined,
            column_highlights: withFormatting ? page.column_highlights : undefined,
            period_highlights: withFormatting ? page.period_highlights : undefined,
            created_at: now,
            updated_at: now,
          }),
        }).then((res) => {
          if (!res.ok) throw new Error("Error duplicating page");
          return res;
        }),
        {
          loading: "Duplicating page...",
          success: "Page duplicated",
          error: "Error duplicating page",
        },
      );
    },
    onError: (_error, { newPageId }) => {
      releasePageInsert(newPageId);
      qry.setQueryData<string[]>(["page_ids", userId], (old) => (old ?? []).filter((pid) => pid !== newPageId));
    },
    onSettled: (_data, _error, { newPageId }) => {
      releasePageInsert(newPageId);
      qry.invalidateQueries({ queryKey: buildQueryKey(userId) });
      qry.invalidateQueries({ queryKey: ["all_products"] });
    },
    onSuccess: async (p) => {
      const res = (await p.json()) as { id?: string; page_name?: string; idx?: string }[];
      const pageData = Array.isArray(res) ? res[0] : res;
      captureEvent("page_duplicated", {
        initial_page: false,
        page_name: pageData?.page_name,
        idx: pageData?.idx,
      });
    },
  });
};

export const useDeletePage = () => {
  const qry = useQueryClient();
  const { getAccessTokenSilently } = useAuth0();
  const userId = useUserId();
  return useMutation({
    networkMode: "always",
    mutationFn: async (id: string) => {
      qry.setQueryData<string[]>(["page_ids", userId], (old) => (old ?? []).filter((pid) => pid !== id));
      const token = await getHasuraToken(getAccessTokenSilently);
      return toast.promise(
        fetch("/api/delete-page", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ pageId: id, userId }),
        }),
        {
          loading: "Deleting page...",
          success: "Page deleted",
          error: "Error deleting page",
        },
        { duration: 10_000 },
      );
    },
    onSettled: () => {
      qry.invalidateQueries({ queryKey: buildQueryKey(userId) });
      qry.invalidateQueries({ queryKey: ["all_products"] });
    },
  });
};

export const useDeletePages = () => {
  const qry = useQueryClient();
  const { getAccessTokenSilently } = useAuth0();
  const userId = useUserId();
  return useMutation({
    networkMode: "always",
    mutationFn: async (pageIds: string[]) => {
      qry.setQueryData<string[]>(["page_ids", userId], (old) => (old ?? []).filter((pid) => !pageIds.includes(pid)));
      const count = pageIds.length;
      const token = await getHasuraToken(getAccessTokenSilently);
      return toast.promise(
        fetch("/api/delete-pages", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ pageIds, userId }),
        }),
        {
          loading: `Deleting ${count} ${count === 1 ? "page" : "pages"}...`,
          success: `${count} ${count === 1 ? "page" : "pages"} deleted`,
          error: "Error deleting pages",
        },
      );
    },
    onSettled: () => {
      qry.invalidateQueries({ queryKey: buildQueryKey(userId) });
      qry.invalidateQueries({ queryKey: ["all_products"] });
    },
  });
};

export const useActivePageId = (): string => useOptionalActivePageId() ?? "";

export const useOptionalActivePageId = (): string | undefined => {
  const pane = usePaneActivePageId();
  const params = useParams({ strict: false }) as { id?: string };
  // TODO! REMOVE `soleGrid` WITH the single-grid page id shim in webapp/pane/context.tsx.
  const soleGrid = useSoleGridPageId();
  return pane?.[0] ?? params.id ?? soleGrid;
};

// Highlights are keyed `<colId>-<period>`; older ones use `<colId>-REL-M0`.
const resolveLegacyCellHighlightKeys = (highlights: TPageFormatting): TPageFormatting =>
  Object.fromEntries(
    Object.entries(highlights).map(([key, value]) => {
      const [colId, relativeRow] = key.split(/-(?=REL-)/);
      const period = relativeRow ? relativeRowToRowId[relativeRow] : undefined;
      return period ? [`${colId}-${period}`, value] : [key, value];
    }),
  );

export const usePageFormattingOptions = (pageId?: string) => {
  const brotli = useSuspenseFn("brotli", getSyncBrotli);

  return useQuery({
    ...pagesQueryOptions(useGraphQLClient(), useQueryClient(), useUserId()),
    select: (data): TPageFormattingOptions => {
      const page = data.find((p) => p.id === pageId);

      const formatting: TPageFormattingOptions = {
        cell_highlights: {},
        column_highlights: {},
        period_highlights: {},
      };
      if (page?.cell_highlights)
        formatting.cell_highlights = resolveLegacyCellHighlightKeys(brotli.decompress(page.cell_highlights, {}));
      if (page?.column_highlights) formatting.column_highlights = brotli.decompress(page.column_highlights, {});
      if (page?.period_highlights) formatting.period_highlights = brotli.decompress(page.period_highlights, {});

      return formatting;
    },
  });
};

export const useUpdatePageFormatting = () => {
  const { optimistic: updatePageOptimistic } = useUpdatePage();
  const brotli = useSuspenseFn("brotli", getSyncBrotli);

  return useCallback(
    (x: Partial<TPageFormattingOptions> & { id: string }) => {
      const next: Writable<Pick<TPage, "cell_highlights" | "column_highlights" | "period_highlights">> = {};
      if (x.cell_highlights) next.cell_highlights = brotli.compress(x.cell_highlights);
      if (x.column_highlights) next.column_highlights = brotli.compress(x.column_highlights);
      if (x.period_highlights) next.period_highlights = brotli.compress(x.period_highlights);

      return updatePageOptimistic({ id: x.id, ...next });
    },
    [updatePageOptimistic, brotli],
  );
};

export const usePageMembers = (pageIds: string[]) => {
  const hasura = useGraphQLClient();
  return useQuery({
    enabled: pageIds.length > 0,
    queryKey: ["page_members", pageIds.join(",")],
    queryFn: async () => {
      if (!pageIds.length) return [];
      const data = await hasura.execute(PageMembersByPageIdsQuery, { pageIds });
      return readFragment(PageMemberFragment, data?.data?.page_member ?? []);
    },
  });
};

export const useAddPageMember = (pageId: string | undefined) => {
  const qry = useQueryClient();
  const hasura = useGraphQLClient();

  return useMutation({
    mutationFn: (userIds: string[]) => {
      if (!pageId) {
        return Promise.reject("No page ID");
      }

      return hasura.execute(PageMemberInsertMany, {
        objects: userIds.map((userId) => ({
          page_id: pageId,
          user_id: userId,
          page_order: "placeholder",
        })),
      });
    },
    onSettled: () => {
      qry.invalidateQueries({ queryKey: ["page_members", pageId] });
    },
  });
};

export type TPageFormattingOptions = {
  cell_highlights: TPageFormatting;
  column_highlights: TPageFormatting;
  period_highlights: TPageFormatting;
};

export type TPageFormatting = Record<
  string,
  {
    color: string | null;
    boldText: boolean;
    invertTextColor: boolean;
  }
>;
